import { RiCloseLine, RiDeleteBinFill } from "react-icons/ri";
import Typography from "../Typography";
import Button from "../Button/Button";

/**
 * Reusable delete confirmation modal.
 * @param {boolean} isOpen - Whether the modal is visible
 * @param {function} onClose - Called when Cancel or overlay is clicked
 * @param {function} onConfirm - Called when Delete is clicked
 * @param {string} title - Modal title (e.g. "Delete client")
 * @param {string} itemName - Name of the item being deleted (shown in message)
 * @param {string} [message] - Optional custom message; if not provided, uses default with itemName
 * @param {boolean} [loading] - Show loading state on confirm button
 * @param {string} [confirmLabel] - Label for confirm button (default "Delete")
 * @param {string} [confirmVariant] - Button variant for confirm (default "danger")
 * @param {string} [loadingLabel] - Loading text on confirm button (default "Deleting…")
 * @param {boolean} [showDeleteIcon] - Show trash icon on confirm button (default true)
 */
const DeleteConfirmModal = ({
  isOpen,
  onClose,
  onConfirm,
  title,
  itemName,
  message,
  loading = false,
  confirmLabel = "Delete",
  confirmVariant = "danger",
  loadingLabel = "Deleting…",
  showDeleteIcon = true,
}) => {
  if (!isOpen) return null;

  const safeItemName = itemName ?? "this item";

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 z-[9999] flex items-center justify-center p-4"
      onClick={() => !loading && onClose()}
    >
      <div
        className="bg-surface-primary rounded-lg shadow-lg w-full max-w-md relative"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between p-4 border-b border-border-primary">
          <Typography variant="h5" className="font-semibold text-text-primary">
            {title}
          </Typography>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => !loading && onClose()}
            disabled={loading}
            className="!border-0 !shadow-none !bg-transparent !p-0 !min-w-0"
            aria-label="Close"
          >
            <RiCloseLine className="w-5 h-5 text-text-secondary" />
          </Button>
        </div>
        <div className="p-4">
          <Typography variant="text2" className="text-text-primary">
            {message ? (
              message
            ) : (
              <>
                Are you sure you want to delete <strong>{safeItemName}</strong>? This action cannot
                be undone.
              </>
            )}
          </Typography>
          <div className="flex justify-end gap-2 mt-6">
            <Button
              variant=""
              className="btn-reset"
              onClick={() => !loading && onClose()}
              disabled={loading}
            >
              Cancel
            </Button>
            <Button
              type="button"
              variant={confirmVariant}
              size="md"
              onClick={onConfirm}
              disabled={loading}
              className="inline-flex items-center justify-center gap-2"
            >
              {loading ? (
                <>
                  <span className="spinner-border spinner-border-sm" role="status" />
                  {loadingLabel}
                </>
              ) : (
                <>
                  {showDeleteIcon ? <RiDeleteBinFill className="w-4 h-4" /> : null}
                  {confirmLabel}
                </>
              )}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DeleteConfirmModal;
